import React, { useContext, useCallback, useState, useEffect } from 'react';
import AsyncSelect from 'react-select/async';
import classNames from 'classnames';
import { ResponsiveSize, ValidationState } from 'acma-spa/dist/Types';
import { reactSelectStyles } from 'acma-spa/dist/pickers/PickerStyles';
import { GroupContext, LabelContext } from 'acma-spa';
import styles from 'acma-spa/dist/pickers/PickerStyles.scss';
import { ClientApiProps } from './ClientApiProps';
import { Acma_amateurradiocallsigntypes, IAvailableCallsign, getApiClient } from '../api';

export interface CallsignPickerProps extends ClientApiProps {
  id?: string;
  value: IAvailableCallsign | null;
  size?: ResponsiveSize;
  state?: ValidationState;
  onChange: (v: IAvailableCallsign | null) => void;
  disabled?: boolean;
  className?: string;
  type: Acma_amateurradiocallsigntypes;
  prefix: string;
}

export const CallsignPicker: React.FC<CallsignPickerProps> = ({ size = 'block', ...props }) => {
  const [defaultOptions, setDefaultOptions] = useState<IAvailableCallsign[]>([]);

  const search = async (includeReserved: boolean, s: string) => {
    if (props.type == null) return [];

    const [client] = getApiClient(props.baseUrl, props.b2cDomainName, props.msalClient);
    var res = await client.callsignApplicationGetAvailable(props.type, includeReserved, `${props.prefix}${s}`);
    return res.map((r) => ({ name: r.name?.substr(3), reservedTo: r.reservedTo ?? '' }));
  };

  useEffect(() => {
    setDefaultOptions([]);
    search(false, '').then((v) => setDefaultOptions(v));
  }, [props.type, props.prefix]);

  useEffect(() => {
    if (props.value && props.value.reservedTo == undefined) {
      search(true, props.value.name ?? '').then((v) => {
        if (v && v.length > 0) {
          props.onChange(v[0]);
        } else {
          props.onChange({ ...props.value, reservedTo: '' });
        }
      });
    }
  }, [props.value]);

  const labelCtx = useContext(LabelContext);
  const groupCtx = useContext(GroupContext);
  const state = labelCtx.state || props.state;

  return (
    <div
      className={classNames(
        styles['au-picker'],
        {
          [styles[`au-picker--width-${size}`]]: size && size != 'block',
          [styles[`au-picker--block`]]: size == 'block',
        },
        groupCtx.className,
        props.className,
      )}>
      <AsyncSelect<IAvailableCallsign>
        inputId={props.id || labelCtx.id}
        styles={reactSelectStyles(state)}
        className={classNames(groupCtx.className, props.className)}
        placeholder={defaultOptions.length > 0 ? `${props.prefix}${defaultOptions[0].name} (Next available)` : 'Next available'}
        loadOptions={(s) => search(true, s)}
        value={props.value}
        onChange={(v) => props.onChange(v)}
        getOptionLabel={(r) => `${props.prefix}${r.name}${r.reservedTo ? '*' : ''}`}
        getOptionValue={(r) => r.name ?? ''}
        isClearable
        isDisabled={props.disabled}
        cacheOptions={false}
        defaultOptions={defaultOptions}
      />
    </div>
  );
};
